import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

export const Quality = () => {
  const { t } = useLang();
  return (
    <section
      id="quality"
      className="relative bg-brand-cream py-24 md:py-32 overflow-hidden"
      data-testid="quality-section"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="grid grid-cols-12 gap-8 items-end mb-16 md:mb-24"
        >
          <div className="col-span-12 md:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="block w-8 h-px bg-brand-red" />
              <span className="font-sans text-[11px] tracking-[0.3em] uppercase text-brand-red">
                {t.quality.label}
              </span>
            </div>
            <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl text-brand-ink leading-[0.95]">
              {t.quality.headline}
              <br />
              <span className="italic text-brand-red">{t.quality.headline2}</span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5">
            <p className="font-sans text-base md:text-lg text-brand-muted leading-relaxed">
              {t.quality.body}
            </p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-brand-ink/15 border border-brand-ink/15">
          {t.quality.steps.map((s, i) => (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="bg-brand-cream p-8 md:p-10 group hover:bg-brand-cream-alt transition-colors duration-500"
              data-testid={`quality-step-${s.id}`}
            >
              <div className="flex items-baseline justify-between mb-8">
                <span className="font-serif text-5xl text-brand-red">{s.id}</span>
                <span className="font-sans text-[10px] tracking-[0.3em] uppercase text-brand-muted">
                  Step
                </span>
              </div>
              <h3 className="font-serif text-2xl text-brand-ink mb-3 group-hover:text-brand-red transition-colors">
                {s.title}
              </h3>
              <p className="font-sans text-sm text-brand-muted leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="mt-12 flex flex-wrap items-center gap-3 md:gap-4"
        >
          {t.quality.badges.map((b, i) => (
            <div
              key={i}
              className="flex items-center gap-2 px-4 py-2 border border-brand-ink/20 rounded-full bg-brand-cream"
              data-testid={`quality-badge-${i}`}
            >
              <ShieldCheck className="w-3.5 h-3.5 text-brand-red" />
              <span className="font-sans text-xs tracking-wider text-brand-ink">{b}</span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
